/**
 * Personalized Referral Landing Page
 * 
 * Shows a personalized invite experience when users click affiliate links.
 * Features:
 * - Blurred portfolio dashboard preview in background
 * - Personalized invitation card with affiliate's display name
 * - Fee discount and signup bonus display
 * - Login/signup CTA
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { LoginModal } from "@/components/auth/login-modal";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/spinner";
import { 
  UserPlusIcon, 
  TrendingUpIcon,
  GiftIcon,
  WalletIcon,
  BarChart3Icon,
  PieChartIcon,
  ActivityIcon,
  DollarSignIcon,
  ClockIcon,
} from "lucide-react";

import { API_BASE_URL as API_BASE } from "@/backend/config";

interface InviteData {
  affiliate_display_name: string | null;
  affiliate_ref_code: string;
  link_slug: string;
  discount_percent: string;
  signup_bonus: string;
}

// Mock Dashboard Card component
const MockCard = ({ 
  title, 
  subtitle, 
  children, 
  className = "",
  colSpan = 1 
}: { 
  title: string; 
  subtitle?: string; 
  children: React.ReactNode; 
  className?: string;
  colSpan?: number;
}) => (
  <div 
    className={`rounded-xl bg-card p-4 md:p-6 border border-border/50 ${colSpan === 2 ? 'md:col-span-2' : ''} ${className}`}
  >
    <header className="mb-4">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </header>
    {children}
  </div>
);

// Mock Portfolio Dashboard - mirrors the actual portfolio page layout
const MockDashboardPreview = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Real-looking Header */}
      <header className="flex items-center gap-3 border-b border-border/50 px-3 py-2 bg-card/80">
        <div className="flex items-center gap-6">
          {/* Logo - using actual Nordex icon */}
          <div className="flex items-center gap-2">
            <img src="/nordex_icon_white.svg" alt="Nordex" className="w-6 h-6" />
            <span className="font-bold text-sm tracking-tight">NORDEX</span>
          </div>
          {/* Navigation */}
          <nav className="hidden md:flex items-center">
            <span className="px-4 py-2.5 text-sm text-foreground/70">Trading</span>
            <span className="px-4 py-2.5 text-sm text-foreground relative">
              Portfolio
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground" />
            </span>
            <span className="px-4 py-2.5 text-sm text-foreground/70">Referrals</span>
            <span className="px-4 py-2.5 text-sm text-foreground/50 relative">
              Leaderboards
              <span className="absolute -top-0.5 -right-1 px-1 py-0.5 text-[7px] font-bold bg-emerald-500 text-white rounded">SOON</span>
            </span>
          </nav>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="h-8 px-3 bg-emerald-500 rounded flex items-center gap-1.5 text-xs text-white font-medium">
            <WalletIcon className="size-3.5" />
            Deposit
          </div>
          <div className="h-8 px-3 bg-muted/50 rounded flex items-center text-xs">
            <span className="text-muted-foreground text-[10px] mr-1.5">Signed in</span>
            <span className="font-medium">8xK2...m4Pq</span>
          </div>
          <div className="h-8 w-8 bg-muted/50 rounded flex items-center justify-center">
            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
          </div>
          <div className="h-8 w-8 bg-muted/50 rounded" />
        </div>
      </header>

      {/* Mock Portfolio Content */}
      <main className="p-4 md:p-6">
        <div className="max-w-[1600px] mx-auto space-y-4 md:space-y-6">
          {/* Portfolio Header Card - true to real design */}
          <div className="rounded-xl bg-card p-4 md:px-6 md:py-5 border border-border/50">
            <div className="flex items-center justify-between">
              {/* Left: Title */}
              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-bold text-foreground">Portfolio Overview</h1>
              </div>
              {/* Center: Tabs */}
              <div className="hidden md:flex items-center gap-1 p-1 bg-muted/50 rounded-lg">
                <span className="px-4 py-1.5 text-sm font-medium bg-emerald-500 text-white rounded-md">Overview</span>
                <span className="px-4 py-1.5 text-sm text-muted-foreground">Performance</span>
                <span className="px-4 py-1.5 text-sm text-muted-foreground">Transfers</span>
                <span className="px-4 py-1.5 text-sm text-muted-foreground">Trades</span>
                <span className="px-4 py-1.5 text-sm text-muted-foreground">Funding</span>
              </div>
              {/* Right: Time Interval */}
              <div className="flex-1 flex justify-end">
                <div className="flex items-center gap-0.5 p-1 bg-muted/50 rounded-lg text-xs">
                  <span className="px-2 py-1 text-muted-foreground">24H</span>
                  <span className="px-2 py-1 text-muted-foreground">7D</span>
                  <span className="px-2 py-1 text-muted-foreground">30D</span>
                  <span className="px-2 py-1 bg-muted rounded font-medium">All</span>
                </div>
              </div>
            </div>
            <p className="text-muted-foreground text-sm mt-1 hidden md:block">Track your trading performance and account health</p>
          </div>

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Portfolio Value Card (2 cols) */}
            <MockCard title="Portfolio Value" subtitle="Total account equity and performance" colSpan={2} className="bg-gradient-to-br from-card via-card to-emerald-500/5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <WalletIcon className="size-4" />
                    <span>Total Equity</span>
                  </div>
                  <div className="text-3xl sm:text-4xl font-bold tracking-tight">$118,458.32</div>
                  <div className="flex items-center gap-1 mt-2 text-sm font-medium text-emerald-500">
                    <TrendingUpIcon className="size-4" />
                    <span>+$1,247.50 (+11.12%)</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Unrealized P&L</div>
                    <div className="text-lg font-semibold text-emerald-500">+$523.45</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Realized P&L</div>
                    <div className="text-lg font-semibold text-emerald-500">+$724.05</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Collateral</div>
                    <div className="text-lg font-semibold">$10,210.82</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">24h Change</div>
                    <div className="text-lg font-semibold text-emerald-500">+$156.20</div>
                  </div>
                </div>
              </div>
            </MockCard>

            {/* Account Health Card */}
            <MockCard title="Account Health" subtitle="Margin and risk metrics">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Health Score</span>
                  <span className="text-2xl font-bold text-emerald-500">92%</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div className="h-full w-[92%] bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full" />
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div>
                    <div className="text-xs text-muted-foreground">Margin Used</div>
                    <div className="text-sm font-semibold">$1,842.50</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Available</div>
                    <div className="text-sm font-semibold">$8,368.32</div>
                  </div>
                </div>
              </div>
            </MockCard>

            {/* Trading Stats Card */}
            <MockCard title="Trading Stats" subtitle="Performance metrics">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <BarChart3Icon className="size-4" />
                    <span className="text-sm">Total Trades</span>
                  </div>
                  <span className="font-semibold">147</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Win Rate</span>
                  <span className="font-semibold text-emerald-500">64.2%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Avg Win</span>
                  <span className="font-semibold text-emerald-500">+$45.20</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Avg Loss</span>
                  <span className="font-semibold text-rose-500">-$28.15</span>
                </div>
              </div>
            </MockCard>

            {/* Position Distribution Card */}
            <MockCard title="Position Distribution" subtitle="Asset allocation">
              <div className="flex items-center justify-center h-32">
                <div className="relative">
                  <PieChartIcon className="size-24 text-muted/30" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-lg font-bold">3</div>
                      <div className="text-xs text-muted-foreground">Positions</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center gap-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-orange-500" />
                  <span>BTC 45%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span>ETH 35%</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-purple-500" />
                  <span>SOL 20%</span>
                </div>
              </div>
            </MockCard>

            {/* Recent Activity Card */}
            <MockCard title="Recent Activity" subtitle="Latest trades">
              <div className="space-y-3">
                {[
                  { pair: "BTC-USD", side: "Long", pnl: "+$124.50", time: "2h ago" },
                  { pair: "ETH-USD", side: "Short", pnl: "-$45.20", time: "5h ago" },
                  { pair: "SOL-USD", side: "Long", pnl: "+$67.80", time: "1d ago" },
                ].map((trade, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <div className="flex items-center gap-2">
                      <ActivityIcon className="size-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{trade.pair}</span>
                      <span className={`text-xs ${trade.side === 'Long' ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {trade.side}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${trade.pnl.startsWith('+') ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {trade.pnl}
                      </div>
                      <div className="text-xs text-muted-foreground">{trade.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </MockCard>

            {/* P&L Chart Card (2 cols) */}
            <MockCard title="P&L History" subtitle="Cumulative profit and loss over time" colSpan={2}>
              <div className="h-48 flex items-end justify-between gap-1 px-2">
                {/* Mock chart bars */}
                {[40, 55, 45, 60, 50, 70, 65, 80, 75, 85, 90, 95].map((height, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-gradient-to-t from-emerald-500/60 to-emerald-400/40 rounded-t"
                      style={{ height: `${height}%` }}
                    />
                    <span className="text-[10px] text-muted-foreground">
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]}
                    </span>
                  </div>
                ))}
              </div>
            </MockCard>

            {/* Fee Summary Card */}
            <MockCard title="Fee Summary" subtitle="Trading costs breakdown">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <DollarSignIcon className="size-4" />
                    <span className="text-sm">Total Fees Paid</span>
                  </div>
                  <span className="font-semibold">$234.56</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <ClockIcon className="size-4" />
                    <span className="text-sm">Funding Received</span>
                  </div>
                  <span className="font-semibold text-emerald-500">+$89.20</span>
                </div>
                <div className="pt-2 border-t border-border/50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Net Cost</span>
                    <span className="font-bold">$145.36</span>
                  </div>
                </div>
              </div>
            </MockCard>
          </div>
        </div>
      </main>
    </div>
  );
};

// Background with blurred dashboard preview and animated orbs
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Blurred Dashboard Preview - same approach as AuthGate */}
      <div className="absolute inset-0 select-none">
        <div className="blur-[3px] opacity-50">
          <MockDashboardPreview />
        </div>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background/80" />
      </div>
      
      {/* Animated gradient orbs on top for extra life - asymmetrical positioning */}
      <div 
        className="absolute top-1/4 -left-32 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"
      />
      <div 
        className="absolute bottom-[12%] -right-24 w-72 h-72 bg-emerald-400/[0.06] rounded-full blur-[100px] animate-pulse"
        style={{ animationDelay: '1s', animationDuration: '3s' }}
      />
      <div 
        className="absolute top-[55%] left-[15%] w-56 h-56 bg-primary/5 rounded-full blur-3xl animate-pulse"
        style={{ animationDelay: '2s', animationDuration: '4s' }}
      />
      
      {/* Center vignette to focus attention on card */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, transparent 60%)'
        }}
      />
    </div>
  );
};

export const InvitePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isReady } = useUnifiedAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Fetch invite data from API
  const { data: inviteData, isLoading, error } = useQuery({
    queryKey: ["invite", slug],
    queryFn: async (): Promise<InviteData | null> => {
      if (!slug) return null;
      const response = await fetch(`${API_BASE}/api/public/invite/${slug}`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error("Failed to fetch invite data");
      return response.json();
    },
    enabled: !!slug,
    staleTime: 60000, // Cache for 1 minute
    retry: 1,
  });

  // Store ref code for signup attribution
  useEffect(() => {
    if (inviteData?.affiliate_ref_code) {
      localStorage.setItem("ref_code", inviteData.affiliate_ref_code);
      localStorage.setItem("ref_campaign", slug || "");
    }
  }, [inviteData, slug]);

  // If authenticated, redirect to main app
  useEffect(() => {
    if (isReady && isAuthenticated) {
      navigate("/trade/BTC-USD", { replace: true });
    }
  }, [isReady, isAuthenticated, navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Spinner />
          <p className="text-muted-foreground mt-4">Loading invite...</p>
        </div>
      </div>
    );
  }

  // Invalid or expired link
  if (error || !inviteData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
            <UserPlusIcon className="size-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Invalid Invite Link</h2>
          <p className="text-muted-foreground mb-6">
            This invite link is invalid or has expired. You can still join Nordex directly.
          </p>
          <Button onClick={() => navigate("/trade/BTC-USD")} size="lg">
            Continue to Nordex
          </Button>
        </div>
      </div>
    );
  }

  const displayName = inviteData.affiliate_display_name || 
    `${inviteData.affiliate_ref_code.slice(0, 4)}...${inviteData.affiliate_ref_code.slice(-4)}`;
  const discountPercent = parseFloat(inviteData.discount_percent);
  const signupBonus = parseFloat(inviteData.signup_bonus);
  const hasDiscount = discountPercent > 0;
  const hasBonus = signupBonus > 0;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* Animated Gradient Background */}
      <AnimatedBackground />

      {/* Centered Invite Card */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4 md:p-8">
        <div className="w-full max-w-lg">
          {/* Main Card - Enhanced with glow and depth */}
          <div className="bg-card/98 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 md:p-8 shadow-[0_0_60px_-15px_rgba(46,189,133,0.3)] ring-1 ring-white/5">
            {/* Header */}
            <div className="text-center mb-6">
              {/* Avatar/Icon - Upward trending arrow */}
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500/20 via-emerald-500/15 to-primary/10 flex items-center justify-center mx-auto mb-4 ring-2 ring-emerald-500/20 ring-offset-2 ring-offset-background">
                <TrendingUpIcon className="size-10 text-emerald-500" />
              </div>

              <p className="text-muted-foreground text-sm mb-1">You've been invited by</p>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-foreground via-emerald-400 to-foreground bg-clip-text text-transparent">
                {displayName}
              </h1>
            </div>

            {/* Benefits Section */}
            <div className="space-y-4 mb-6">
              {/* Hero Fee Savings - Only show if there's an actual discount */}
              {hasDiscount && (
                <div className="bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent rounded-xl p-4 border border-emerald-500/25 text-center">
                  <p className="text-[10px] uppercase tracking-wider text-foreground/80 font-medium mb-1">
                    Your Exclusive Discount
                  </p>
                  <p className="text-4xl font-bold text-emerald-400 tracking-tight">
                    {discountPercent.toFixed(0)}%
                  </p>
                  <p className="text-foreground/70 text-xs mt-0.5">OFF on trading fees</p>
                </div>
              )}

              {/* Signup bonus - if any */}
              {hasBonus && (
                <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/20 text-center flex items-center justify-center gap-2">
                  <GiftIcon className="size-4 text-amber-500" />
                  <p className="font-semibold text-amber-500">
                    + ${signupBonus.toFixed(2)} Signup Bonus
                  </p>
                </div>
              )}

              {/* Compact Platform Features - Side by Side */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-muted/20 rounded-lg py-3 px-2 border border-border/30 text-center">
                  <p className="font-medium text-sm">Lowest DEX Fees</p>
                  <p className="text-[11px] text-muted-foreground">Industry-leading rates</p>
                </div>
                <div className="bg-muted/20 rounded-lg py-3 px-2 border border-border/30 text-center">
                  <p className="font-medium text-sm">Up to 100x</p>
                  <p className="text-[11px] text-muted-foreground">Non-custodial trading</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <Button 
              onClick={() => setLoginModalOpen(true)} 
              size="lg"
              className="w-full text-lg py-6 bg-[#2ebd85] hover:bg-[#2ebd85]/90 text-white border-0 shadow-lg shadow-emerald-500/20"
            >
              <UserPlusIcon className="size-5 mr-2" />
              Join Nordex Now
            </Button>

            {/* Terms note - inside card */}
            <p className="text-center text-[10px] text-muted-foreground mt-3">
              By joining, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>

      <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
    </div>
  );
};

export default InvitePage;

